import { classNames } from "../../utils/classNames";
import { Icon, IconProps } from "../Icon";
import { Combobox } from "@headlessui/react";
import { FloatingOverlay } from "../FloatingOverlay";
import {
  ChangeEvent,
  FocusEventHandler,
  InputHTMLAttributes,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { useFloatingDropdown } from "../floatingHelpers";
import { IconButton } from "../Button";
import { defaultFilterFn } from "./autocompleteHelpers";
import { SelectItem, SelectItemProps } from "../Select";
import { preventDefault } from "../../utils/events";
import { IconName } from "../iconMapping";
import { isNil } from "lodash";

import { useTranslation } from "../../stores/RootStoreContext";

export type AutocompleteInputUIProps = {
  open: boolean;
  icon?: IconName;
  onClear?: () => void;
  error?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  onInputBlur?: FocusEventHandler<HTMLInputElement>;
};

export type AutocompleteProps<T> = {
  id?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  icon?: IconProps["name"];
  name?: string;
  value?: T | null;
  inputValue?: string;
  renderInputValue?: (item: T) => string;
  items: T[];
  onSelect: (item: T) => void;
  onClear?: () => void;
  onInputValueChange?: (value: string) => void;
  onInputBlur?: FocusEventHandler<HTMLInputElement>;
  idFn: (item: T) => string;
  labelFn: (item: T) => string;
  iconFn?: (item: T) => SelectItemProps["icon"];
  captionFn?: (item: T) => SelectItemProps["caption"];
  filterFn?: (
    { items, labelFn }: { items: T[]; labelFn: (item: T) => string },
    inputValue: string,
  ) => T[];
  fullWidth?: boolean;
  error?: boolean;
  message?: string;
  autofocus?: boolean;
  autocomplete?: string;
  onMenuClose?: () => void;
  /** Note: the rendered component MUST include an <AutocompleteInput/> element, not a regular <input/> element */
  renderInput?: (props: AutocompleteInputUIProps) => ReactElement;
};

export function AutocompleteInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <Combobox.Input
      className={
        className ??
        "tw-flex-1 focus-visible:tw-outline-none tw-py-3 tw-w-full bg-transparent disabled:bg-transparent tw-text-left tw-whitespace-nowrap"
      }
      {...props}
    />
  );
}

function DefaultAutocompleteInputUI(props: AutocompleteInputUIProps) {
  const { t } = useTranslation();
  return (
    <div
      className={classNames(
        "tw-relative",
        "tw-flex",
        "tw-items-center",
        "tw-gap-2",
        "tw-px-4",
        "tw-leading-tight",
        "tw-border",
        "tw-rounded",
        "focus-within:tw-ring-1",
        "tw-w-full",
        "tw-cursor-pointer",
        props.error
          ? [
              "color-default",
              "bg-default",
              "border-error",
              "focus-within:border-error",
              "focus-within:ring-error",
              "hover:border-error",
              "hover:focus-within:border-error",
            ]
          : props.inputProps?.disabled
            ? ["subtle", "bg-gray-x-soft", "border-gray-soft", "hover:border-gray-soft"]
            : [
                "color-default",
                "bg-default",
                "border-gray",
                "focus-within:border-primary",
                "focus-within:ring-primary",
                "hover:bg-border-gray-hard",
                "hover:focus-within:border-primary",
              ],
      )}
    >
      {props.icon && (
        <div className={classNames("text-xl tw-leading-5 tw-h-5 subtle")}>
          <Icon name={props.icon} />
        </div>
      )}
      <AutocompleteInput data-testid={"selectInput"} {...props.inputProps} />
      {!props.inputProps?.disabled && !props.open && props.onClear && props.inputProps?.value && (
        <div>
          <IconButton
            color={"inherit"}
            icon={"x"}
            tooltip={t("clear")}
            onClick={preventDefault(props.onClear)}
          />
        </div>
      )}
      {!props.inputProps?.disabled && (
        <div className={classNames("text-xl tw-leading-5 tw-h-5 tw-pointer-events-none")}>
          <Icon name={props.open ? "caret-up" : "caret-down"} />
        </div>
      )}
    </div>
  );
}

function defaultRenderInput(props: AutocompleteInputUIProps) {
  return <DefaultAutocompleteInputUI {...props} />;
}

export function Autocomplete<T>({
  onMenuClose,
  renderInput = defaultRenderInput,
  renderInputValue,
  ...props
}: AutocompleteProps<T>) {
  const { t } = useTranslation();
  const { refs, floatingStyles } = useFloatingDropdown();
  const { labelFn, value } = props;
  const [inputValue, setInputValue] = useState(
    props.inputValue !== undefined
      ? props.inputValue
      : !isNil(value)
        ? renderInputValue
          ? renderInputValue(value)
          : labelFn(value)
        : "",
  );

  const isControlled = props.inputValue !== undefined;
  const filteredItems = isControlled
    ? props.items
    : (props.filterFn?.(props, inputValue) ?? defaultFilterFn(props, inputValue));

  const handleInputValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isControlled) {
      props.onInputValueChange?.(event.target.value);
    } else {
      setInputValue(event.target.value);
    }
  };

  const handleChange = (item: T | null) => {
    if (item === null) {
      props.onClear?.();
      if (!isControlled) {
        setInputValue("");
      }
    } else {
      props.onSelect(item);
      if (!isControlled) {
        setInputValue(renderInputValue ? renderInputValue(item) : labelFn(item));
      }
    }
  };

  const handleClear =
    props.onClear &&
    (() => {
      props.onClear?.();
      if (!isControlled) {
        setInputValue("");
      }
    });

  useEffect(() => {
    if (props.inputValue !== undefined) {
      setInputValue(props.inputValue);
    } else {
      setInputValue(value ? (renderInputValue ? renderInputValue(value) : labelFn(value)) : "");
    }
    // Note: we only want to react to changes of `value`, and `labelFn` might always change, so don't include it in the deps list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, props.inputValue]);

  return (
    <Combobox value={value ?? null} onChange={handleChange} nullable disabled={props.disabled}>
      {({ open }) => (
        <>
          <div className={classNames(props.fullWidth && "tw-w-full")}>
            {props.label && <label className={"tw-block tw-mb-1"}>{props.label}</label>}
            <Combobox.Button
              as={"div"}
              ref={(ref) => {
                refs.setReference(ref);
                if (props.autofocus) {
                  ref?.click();
                }
              }}
              className={"tw-grow"}
              id={props.id}
            >
              {renderInput({
                open,
                icon: props.icon,
                error: props.error,
                onClear: handleClear,
                inputProps: {
                  value: inputValue,
                  onChange: handleInputValueChange,
                  name: props.name,
                  placeholder: props.placeholder,
                  disabled: props.disabled,
                  autoFocus: props.autofocus,
                  autoComplete: props.autocomplete ?? "off",
                  onBlur: props.onInputBlur,
                },
              })}
            </Combobox.Button>
            {props.message && (
              <div
                className={classNames(
                  "tw-pt-1",
                  "tw-pl-4",
                  "text-sm",
                  props.error ? "color-error" : "subtle",
                )}
              >
                {props.message}
              </div>
            )}
          </div>

          {open && (inputValue || filteredItems.length > 0) && (
            <FloatingOverlay layer={"dropdown"} modal onUnmount={onMenuClose}>
              <Combobox.Options
                static
                as="div"
                ref={refs.setFloating}
                className={classNames(
                  "tw-rounded",
                  "tw-border",
                  "border-gray",
                  "tw-shadow",
                  "bg-default",
                  "tw-inline-flex",
                  "tw-flex-col",
                  "tw-py-1",
                  "tw-overflow-auto",
                )}
                style={floatingStyles}
              >
                {filteredItems.length === 0 && inputValue !== "" ? (
                  <SelectItem label={t("noOptions")} />
                ) : (
                  filteredItems.map((item) => (
                    <Combobox.Option as="div" key={props.idFn(item)} value={item}>
                      {({ active }) => (
                        <SelectItem
                          label={labelFn(item)}
                          icon={props.iconFn?.(item)}
                          caption={props.captionFn?.(item)}
                          active={active}
                        />
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </FloatingOverlay>
          )}
        </>
      )}
    </Combobox>
  );
}
